import {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ISetFilterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import uniq from 'lodash/uniq'
import { ChevronRightIcon, ShieldAlert } from 'lucide-react'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { Control, SmartQuestionStatus } from '@/gen/inventory/v1/control_service_pb'

import { controlStatusLabel, smartQuestionStatusLabel } from '@/const/label'
import { smartQuestionStatusPriority } from '@/const/priority'

import { calculatePercentage } from '@/lib/collection/math-utils'
import { CONTROL_STATUS } from '@/lib/connect/proto-types'
import { cn } from '@/lib/ui/style-helpers'

import { ControlCell } from '@/pages/control/ag-grid-control/components/cells/control-cell'
import { ControlEvidenceRequestCell } from '@/pages/control/ag-grid-control/components/cells/control-evidence-request-cell'
import { ControlScopeCell } from '@/pages/control/ag-grid-control/components/cells/control-scope-cell'
import { ControlStatusCell } from '@/pages/control/ag-grid-control/components/cells/control-status-cell'
import { ControlWeightCell } from '@/pages/control/ag-grid-control/components/cells/control-weight-cell'
import {
  WIDTH_CATEGORY,
  WIDTH_CATEGORY_FILL,
  WIDTH_CONTROL,
  WIDTH_CONTROL_WEIGHT,
  WIDTH_EVIDENCE_REQUEST,
  WIDTH_GAPS,
  WIDTH_SCOPE,
  WIDTH_THIRD_PARTY_ADHERENCE,
  WIDTH_VALIDATION_STATUS,
} from '@/pages/control/ag-grid-control/config/column-sizes'
import { CONTROL_COLUMN_HEADERS } from '@/pages/control/ag-grid-control/labels'
import type { ControlRow } from '@/pages/control/ag-grid-control/types'

import { Progress } from '@/components/lemmala-design-system/progress/progress'
import { numericComparator } from '@/components/lemmala-design-system/table/utils/comparators'
import { Sentiment } from '@/components/lemmala-design-system/variance-percentage/types'

export const getControlColumnDefs = (
  controls: Control[] = [],
  readOnly = false,
  hasManagePermissions = false,
  hideEvidenceRequest = true,
  onToggleGroup?: (scopeName: string) => void,
  groupByScope = false,
  hideWeight = false,
  from?: string,
): ColDef<ControlRow>[] => {
  const cols: ColDef<ControlRow>[] = [
    // Hidden column for scope filtering
    {
      colId: 'scope',
      field: 'scope',
      headerName: CONTROL_COLUMN_HEADERS.SCOPE,
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: uniq(controls.map((control) => control.scope?.name ?? '')),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ControlRow>) => {
        // Group headers return only scopes that exist in their group
        if (params.data?.isGroupHeader) {
          return params.data.categoryFilterValues?.scopes || []
        }
        return params.data?.scope?.name ?? ''
      },
    },
    // Control column (with scope group header rendering when groupByScope)
    {
      colId: 'title',
      // No value while grouped: AG Grid skips refreshing a cell whose value did not change, and a
      // scope header keeps the same value across expand/collapse, which left its chevron stale.
      field: groupByScope ? undefined : 'title',
      headerName: CONTROL_COLUMN_HEADERS.CONTROL,
      minWidth: WIDTH_CONTROL,
      width: groupByScope ? undefined : WIDTH_CONTROL,
      flex: groupByScope ? 1 : null,
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // When grouped by scope: render scope header (categoryName holds scope display name)
        if (groupByScope && params.data?.isGroupHeader) {
          const { categoryName, isExpanded } = params.data

          return (
            <button
              onClick={() => onToggleGroup?.(categoryName || '')}
              className='flex size-full items-center gap-1.5 pr-4'
            >
              <div className='text-main-foreground-primary'>
                <ChevronRightIcon
                  className={cn(
                    'size-4 transition-transform duration-200',
                    isExpanded && 'rotate-90',
                  )}
                />
              </div>
              <span className='text-main-foreground-primary text-label-default'>
                {categoryName}
              </span>
            </button>
          )
        }
        // Regular control cell
        return <ControlCell {...params} />
      },
      colSpan: (params: { data?: ControlRow }) => {
        // Only span when NOT grouped
        if (!groupByScope && params.data?.isGroupHeader) {
          return hideEvidenceRequest ? 2 : 3 // 2 columns if evidence hidden, 3 otherwise
        }
        return 1
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
        const { title, description } = params.data || {}
        return `${title || ''} ${description || ''}`
      },
      valueGetter: groupByScope
        ? undefined
        : (params: ValueGetterParams<ControlRow>) => params.data?.title || '',
      sortable: !groupByScope,
      cellDataType: false,
    },
    {
      colId: 'scopeColumn',
      field: 'scope',
      headerName: CONTROL_COLUMN_HEADERS.SCOPE,
      hide: groupByScope,
      width: WIDTH_SCOPE,
      sortable: true,
      cellRenderer: ControlScopeCell,
      valueGetter: (params: ValueGetterParams<ControlRow>) => params.data?.scope?.name ?? '',
      cellDataType: false,
    },
    {
      colId: 'controlWeight',
      field: 'controlWeight',
      headerName: CONTROL_COLUMN_HEADERS.WEIGHT,
      hide: groupByScope || hideWeight,
      width: WIDTH_CONTROL_WEIGHT,
      sortable: true,
      comparator: numericComparator,
      initialSort: 'desc',
      cellRenderer: ControlWeightCell,
      cellDataType: false,
    },
    // Category column (only when groupByScope is false)
    {
      colId: 'category',
      field: 'categoryName',
      headerName: CONTROL_COLUMN_HEADERS.CATEGORY,
      hide: groupByScope,
      minWidth: hideEvidenceRequest ? WIDTH_CATEGORY_FILL : WIDTH_CATEGORY,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // Only render for regular control rows (not group headers)
        if (params.data?.isGroupHeader) {
          return null
        }
        const categoryName = params.data?.categoryName || ''
        return (
          <div className='flex size-full items-center text-main-foreground-primary text-body-default'>
            {categoryName || '–'}
          </div>
        )
      },
      valueGetter: (params: ValueGetterParams<ControlRow>) => {
        return params.data?.categoryName || ''
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
        // For group headers, return empty string
        if (params.data?.isGroupHeader) {
          return ''
        }
        return params.data?.categoryName || ''
      },
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const categoryA = nodeA?.data?.categoryName || ''
        const categoryB = nodeB?.data?.categoryName || ''
        return categoryA.localeCompare(categoryB)
      },
      cellDataType: false,
    },
    {
      colId: 'adherenceScope',
      headerName: CONTROL_COLUMN_HEADERS.CONTROL_ADHERENCE,
      hide: !groupByScope,
      minWidth: WIDTH_THIRD_PARTY_ADHERENCE,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // Only render for group headers
        if (params.data?.isGroupHeader) {
          const { categoryMetrics } = params.data
          if (!categoryMetrics) return null

          const { totalValidated, total } = categoryMetrics

          return (
            <div className='flex size-full items-center gap-6 pr-2'>
              <Progress
                value={calculatePercentage({ amount: totalValidated, total })}
                className='h-1.75 w-full'
                sentiment={Sentiment.POSITIVE}
              />
              <span className='text-nowrap text-body-default'>
                {totalValidated} / {total}
              </span>
            </div>
          )
        }
        // Empty for regular control rows
        return null
      },
      cellDataType: false,
    },
    // Hidden column for status filtering (always named "Validation status" in filter bar)
    {
      colId: 'status',
      field: 'status',
      headerName: CONTROL_COLUMN_HEADERS.VALIDATION_STATUS,
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: CONTROL_STATUS.map((status) => controlStatusLabel[status.number as ControlStatus]),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ControlRow>) => {
        if (params.data?.isGroupHeader) {
          return params.data.categoryFilterValues?.statuses || []
        }
        const status = params.data?.status
        return status === undefined ? '' : controlStatusLabel[status]
      },
    },
    // Visible Validation status / Gaps column
    {
      colId: 'statusDisplay',
      field: 'status',
      headerName: groupByScope
        ? CONTROL_COLUMN_HEADERS.GAPS
        : CONTROL_COLUMN_HEADERS.VALIDATION_STATUS,
      minWidth: groupByScope ? WIDTH_GAPS : WIDTH_VALIDATION_STATUS,
      width: groupByScope ? WIDTH_GAPS : undefined,
      flex: groupByScope ? null : 1,
      cellStyle: { paddingLeft: 0, paddingRight: 0 },
      cellRenderer: (params: ICellRendererParams<ControlRow>) => {
        // When grouped by scope, show gaps indicator for group headers
        if (groupByScope && params.data?.isGroupHeader) {
          const { categoryMetrics } = params.data
          if (!categoryMetrics) return null

          const { gapsCount } = categoryMetrics

          return (
            <div className='flex size-full items-center text-body-default'>
              {gapsCount > 0 ? (
                <div className='flex items-center gap-1 text-main-foreground-primary'>
                  {gapsCount} <ShieldAlert className={`size-4 text-main-foreground-danger`} />
                </div>
              ) : (
                <span className='text-main-foreground-tertiary'>–</span>
              )}
            </div>
          )
        }

        // Regular status cell for control rows
        return (
          <ControlStatusCell
            {...params}
            readOnly={readOnly}
            hasManagePermissions={hasManagePermissions}
            from={from}
          />
        )
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
        if (params.data?.isGroupHeader) {
          return ''
        }
        const status = params.data?.status
        if (status === undefined) return ''
        return controlStatusLabel[status] || ''
      },
      sortable: !groupByScope,
      cellDataType: false,
    },
    // Evidence Request column
    {
      colId: 'smartQuestion',
      headerName: CONTROL_COLUMN_HEADERS.SMART_EVIDENCE_REQUEST,
      minWidth: WIDTH_EVIDENCE_REQUEST,
      hide: hideEvidenceRequest || groupByScope,
      cellRenderer: ControlEvidenceRequestCell,
      filter: 'agSetColumnFilter',
      flex: hideEvidenceRequest ? 1 : 1.25,
      filterParams: hideEvidenceRequest
        ? undefined
        : ({
            values: uniq(
              controls.map(
                (control) =>
                  smartQuestionStatusLabel[
                    control.smartQuestion?.status || SmartQuestionStatus.UNSPECIFIED
                  ],
              ),
            ),
            suppressMiniFilter: true,
            selectAllOnMiniFilter: true,
            suppressSyncValuesAfterDataChange: true,
          } as ISetFilterParams),
      valueGetter: (params: ValueGetterParams<ControlRow>) => {
        // Group headers return only smart question statuses that exist in their category
        if (params.data?.isGroupHeader) {
          const statuses = params.data.categoryFilterValues?.smartQuestionStatuses || []
          // Return array for filtering (ag-Grid can handle arrays in valueGetter for set filters)
          return statuses.map((status) => smartQuestionStatusLabel[status as SmartQuestionStatus])
        }
        const status = params.data?.smartQuestion?.status || SmartQuestionStatus.UNSPECIFIED
        return smartQuestionStatusLabel[status]
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
        // For group headers, return empty string (don't include in search)
        if (params.data?.isGroupHeader) {
          return ''
        }
        // For regular rows, return the status label
        const status = params.data?.smartQuestion?.status || SmartQuestionStatus.UNSPECIFIED
        return smartQuestionStatusLabel[status] || ''
      },
      sortable: false,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        // When grouped by scope, sort by scope to keep groups intact
        if (groupByScope) {
          const categoryA = nodeA?.data?.categoryName || ''
          const categoryB = nodeB?.data?.categoryName || ''
          return categoryA.localeCompare(categoryB)
        }
        // When not grouped, sort by status priority
        const statusA = nodeA?.data?.smartQuestion?.status || SmartQuestionStatus.UNSPECIFIED
        const statusB = nodeB?.data?.smartQuestion?.status || SmartQuestionStatus.UNSPECIFIED
        return smartQuestionStatusPriority[statusA] - smartQuestionStatusPriority[statusB]
      },
      cellDataType: false,
    },
  ]

  return cols
}
